/**
 * PageLineResolver.ts
 * 
 * Extracts lines from page layouts, handling both NEW and OLD WASM formats:
 * - NEW FORMAT: PageLayoutWithLines with resolved `.lines` array
 * - OLD FORMAT: PageLayout with `.paragraphs` slice references
 * 
 * This centralizes the format detection and line extraction logic that was
 * previously embedded in renderText's Priority 1 path.
 * 
 * @see typesetter-rs/wasm/src/lib.rs - paginate_from_cache returns NEW format
 * @see typesetter-rs/core/src/pagination.rs - PageLayout vs PageLayoutWithLines
 */

import { CanvasLogger } from '../../../../../../../utils/loggers/CanvasLogger';

// =============================================================================
// Types
// =============================================================================

/**
 * A paragraph slice reference (OLD format)
 * Points to a range of lines within a cached paragraph snapshot
 */
export interface ParagraphSlice {
  paragraph_id: string;
  line_start: number;
  line_end: number;
}

/**
 * Page layout with slice references (OLD format from paginate_window_from_frontier)
 */
export interface PageLayoutOldFormat {
  page_index: number;
  paragraphs: ParagraphSlice[];
  content_rect_fp?: any;
  page_hash?: string;
}

/**
 * Page layout with resolved lines (NEW format from paginate_from_cache)
 */
export interface PageLayoutNewFormat {
  page_index: number;
  lines: any[];
  caret_map?: any[];
  content_rect_fp?: any;
  page_hash?: string;
}

/**
 * Union type for either format
 */
export type PageLayoutAny = PageLayoutOldFormat | PageLayoutNewFormat | any;

/**
 * Cached paragraph snapshot from TypesetterWorkerClient
 */
export interface CachedParagraphSnapshot {
  snapshot_id?: string;
  paragraph_id?: string;
  lines?: any[];
  caret_map?: any[];
}

/**
 * Options for resolving page lines
 */
export interface PageLineResolverOptions {
  /** Current page index being resolved */
  pageIndex: number;
  /** Current document text length (for stale line filtering) */
  committedLength: number;
  /** Enable verbose logging */
  enableLogging?: boolean;
  /** Additional context for debug logs (e.g., incrementalPages metadata) */
  debugContext?: {
    totalPages?: number;
    pagesInArray?: number;
    allPageIndices?: number[];
    timestamp?: number;
    /** Whether WASM-resolved lines were used (vs cached snapshots fallback) */
    usedWasmResolvedLines?: boolean;
  };
}

/**
 * Result from resolving page lines
 */
export interface PageLineResolverResult {
  /** Resolved lines for this page */
  lines: any[];
  /** Caret map entries for this page (if available) */
  caretMap: any[];
  /** Which format was used */
  format: 'new' | 'old';
  /** Debug info about the resolution process */
  diagnostics: {
    rawLinesCount: number;
    staleLinesFiltered: number;
    sliceExtractionInfo?: SliceDebugInfo[];
    unmatchedSliceCount?: number;
  };
}

/**
 * Debug info for slice extraction (OLD format)
 */
interface SliceDebugInfo {
  paragraph_id: string;
  line_start: number;
  line_end: number;
  actual_lines: number;
  extracted_lines: number;
}

// =============================================================================
// Main Entry Point
// =============================================================================

/**
 * Resolve page lines from a page layout, handling both NEW and OLD formats.
 * 
 * NEW FORMAT (PageLayoutWithLines):
 * - WASM returns resolved `.lines` array directly
 * - No paragraph_id lookup needed
 * - Filters stale lines with invalid grapheme indices
 * 
 * OLD FORMAT (PageLayout):
 * - WASM returns `.paragraphs` slice references
 * - Requires lookup in cachedSnapshots by paragraph_id
 * - Includes fallback logic for ID mismatches
 * 
 * @param pageLayout - The page layout from WASM (can be either format)
 * @param cachedSnapshots - Cached paragraph snapshots for OLD format lookup
 * @param options - Resolution options including pageIndex and committedLength
 * @returns Resolved lines and caret map for this page
 */
export function resolvePageLines(
  pageLayout: PageLayoutAny,
  cachedSnapshots: CachedParagraphSnapshot[],
  options: PageLineResolverOptions
): PageLineResolverResult {
  const { pageIndex, committedLength, enableLogging = false, debugContext } = options;

  // Check if WASM returned resolved lines (new format) or slice references (old format)
  const hasResolvedLines = pageLayout?.lines && Array.isArray(pageLayout.lines);

  // 🔬 DEBUG: Log the format detection
  if (enableLogging) {
    console.log(`🔬 [PageLineResolver] Page ${pageIndex} format detection:`, {
      hasResolvedLines,
      linesCount: pageLayout?.lines?.length ?? 0,
      paragraphsCount: pageLayout?.paragraphs?.length ?? 0,
      lineRanges: (pageLayout?.lines ?? []).slice(0, 10).map((l: any) => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', ') + ((pageLayout?.lines?.length ?? 0) > 10 ? '...' : ''),
      pageTimestamp: debugContext?.timestamp
    });
  }

  if (hasResolvedLines) {
    return resolveFromNewFormat(pageLayout, options, cachedSnapshots);
  } else {
    return resolveFromOldFormat(pageLayout, cachedSnapshots, options);
  }
}

// =============================================================================
// NEW FORMAT Resolution
// =============================================================================

/**
 * Resolve lines from NEW format (PageLayoutWithLines)
 * WASM returned resolved lines directly - just filter stale ones
 *
 * 🔧 FIX (2026-01-23): WASM pagination lines may have incorrect font_fingerprint
 * because buildMultiParagraphInput ignores formatting spans. We merge the correct
 * runs from cachedSnapshots (which come from layoutAffectedParagraphs with proper
 * formatting spans) into the WASM-resolved lines.
 */
function resolveFromNewFormat(
  pageLayout: PageLayoutNewFormat,
  options: PageLineResolverOptions,
  cachedSnapshots?: CachedParagraphSnapshot[]
): PageLineResolverResult {
  const { pageIndex, committedLength, enableLogging } = options;

  // 🔧 FIX (2026-01-23): Build a lookup map from cached snapshots for font merging.
  // WASM pagination lines have correct Y positions but wrong font_fingerprint (only Inter-Regular).
  // Cached snapshots from layoutAffectedParagraphs have correct font_fingerprint (Inter-Bold, etc).
  // We merge by matching lines on grapheme indices and copying runs from cached.
  const cachedLinesByRange = new Map<string, any>();
  if (cachedSnapshots && cachedSnapshots.length > 0) {
    for (const snapshot of cachedSnapshots) {
      for (const line of (snapshot.lines || [])) {
        const key = `${line.start_grapheme_index}-${line.end_grapheme_index}`;
        cachedLinesByRange.set(key, line);
      }
    }
  }

  // 🛡️ FIX (2026-01-12): Filter out STALE lines with invalid grapheme indices.
  // When DELETE operations shrink the document, cached pages may contain lines
  // with grapheme indices beyond the current document length. These cause
  // DUPLICATE_LINE_RANGES errors and visual text overlap.
  const rawLines = pageLayout.lines || [];
  const validLines: any[] = [];
  let staleLinesFiltered = 0;
  let runsMerged = 0;

  for (const line of rawLines) {
    const isValid = line.end_grapheme_index <= committedLength;
    if (isValid) {
      // 🔧 FIX (2026-01-23): Merge runs from cached snapshot if available
      // This ensures correct font_fingerprint for bold/italic text rendering
      const key = `${line.start_grapheme_index}-${line.end_grapheme_index}`;
      const cachedLine = cachedLinesByRange.get(key);

      if (cachedLine && cachedLine.runs) {
        // Keep WASM's Y positions but use cached line's runs (with correct font_fingerprint)
        const mergedLine = {
          ...line,
          runs: cachedLine.runs
        };
        validLines.push(mergedLine);
        runsMerged++;
      } else {
        validLines.push(line);
      }
    } else {
      staleLinesFiltered++;
      if (enableLogging) {
        CanvasLogger.lines.staleFiltered(pageIndex, 1, committedLength, {
          lineRange: `[${line.start_grapheme_index}-${line.end_grapheme_index})`,
          reason: 'end_grapheme_index > committedLength'
        });
      }
    }
  }

  // 🔬 DEBUG: Log font merge stats
  if (enableLogging || runsMerged > 0) {
    CanvasLogger.glyph.warm('font-merge', runsMerged, {
      pageIndex,
      totalLines: validLines.length,
      linesMerged: runsMerged,
      cachedLinesAvailable: cachedLinesByRange.size
    });
  }

  // Log the result with y_position tracking for coalesce bug detection
  const linePositions = validLines.map((line: any) => ({
    start: line.start_grapheme_index,
    end: line.end_grapheme_index,
    y: (line.baseline_y_fp || 0) / 64
  }));
  
  CanvasLogger.lines.positions(pageIndex, linePositions, {
    format: 'NEW',
    rawLinesCount: rawLines.length,
    staleLinesFiltered,
    committedLength,
    hasCaretMap: !!pageLayout.caret_map,
    caretMapCount: pageLayout.caret_map?.length ?? 0
  });
  
  // Also log stale filter summary if any were filtered
  if (staleLinesFiltered > 0) {
    CanvasLogger.lines.staleFiltered(pageIndex, staleLinesFiltered, committedLength, {
      rawLinesCount: rawLines.length,
      validLinesCount: validLines.length
    });
  }

  return {
    lines: validLines,
    caretMap: pageLayout.caret_map || [],
    format: 'new',
    diagnostics: {
      rawLinesCount: rawLines.length,
      staleLinesFiltered
    }
  };
}

// =============================================================================
// OLD FORMAT Resolution (Backwards Compatibility)
// =============================================================================

/**
 * Resolve lines from OLD format (PageLayout with paragraph slices)
 * Requires lookup in cachedSnapshots by paragraph_id
 */
function resolveFromOldFormat(
  pageLayout: PageLayoutOldFormat,
  cachedSnapshots: CachedParagraphSnapshot[],
  options: PageLineResolverOptions
): PageLineResolverResult {
  const { pageIndex, enableLogging, debugContext } = options;

  // 🔧 OLD FORMAT FALLBACK: Use slice references (for backwards compatibility)
  // This path should eventually be removed once all code uses new format
  CanvasLogger.lines.oldFormatFallback(pageIndex);

  const lines: any[] = [];
  const sliceDebugInfo: SliceDebugInfo[] = [];

  // 🐛 DEBUG: Log what slices we received for this page
  const allPageIndicesInArray = debugContext?.allPageIndices ?? [];
  const sliceBoundsInfo = (pageLayout?.paragraphs || []).map((s: any) => ({
    paragraph_id: s.paragraph_id?.substring(0, 8),
    line_start: s.line_start,
    line_end: s.line_end
  }));

  CanvasLogger.lines.sliceExtraction(pageIndex, 0, {
    phase: 'start',
    totalPages: debugContext?.totalPages,
    pagesInArray: debugContext?.pagesInArray ?? 0,
    allPageIndicesInArray,
    thisPageHasLayout: !!pageLayout,
    foundPageIndex: pageLayout?.page_index,
    paragraphSlicesCount: pageLayout?.paragraphs?.length ?? 0,
    sliceBounds: sliceBoundsInfo,
    cachedSnapshotsCount: cachedSnapshots.length
  });

  // 🔬 DEBUG: Log ID mismatch diagnostics
  const cachedIds = cachedSnapshots.map(s => s.paragraph_id || s.snapshot_id);
  const sliceIds = (pageLayout?.paragraphs || []).map((s: any) => s.paragraph_id);
  const unmatchedSlices = sliceIds.filter((id: string) => !cachedIds.includes(id));

  if (unmatchedSlices.length > 0) {
    CanvasLogger.lines.idMismatch(pageIndex, unmatchedSlices.length, {
      unmatchedSliceIds: unmatchedSlices.slice(0, 5),
      cachedIds: cachedIds.slice(0, 5),
      totalSlices: sliceIds.length,
      totalCached: cachedIds.length
    });
  }

  // Process each paragraph slice
  for (const slice of pageLayout?.paragraphs || []) {
    const paragraphSnapshot = cachedSnapshots.find(s =>
      s.snapshot_id === slice.paragraph_id ||
      s.paragraph_id === slice.paragraph_id
    ) || cachedSnapshots[0];

    // 🔬 DEBUG: Log when fallback is used
    const usedFallback = !cachedSnapshots.find(s =>
      s.snapshot_id === slice.paragraph_id ||
      s.paragraph_id === slice.paragraph_id
    );
    if (usedFallback && enableLogging) {
      CanvasLogger.lines.idMismatch(pageIndex, 1, {
        sliceId: slice.paragraph_id?.slice(0, 8),
        fallbackId: (cachedSnapshots[0]?.paragraph_id || cachedSnapshots[0]?.snapshot_id)?.slice(0, 8),
        reason: 'used_fallback_snapshot'
      });
    }

    if (paragraphSnapshot?.lines) {
      // 🔍 SAFETY CHECK: Validate slice bounds
      const actualLineCount = paragraphSnapshot.lines.length;
      const safeLineStart = Math.min(slice.line_start, actualLineCount);
      const safeLineEnd = Math.min(slice.line_end, actualLineCount);

      const sliceLines = paragraphSnapshot.lines.slice(safeLineStart, safeLineEnd);

      sliceDebugInfo.push({
        paragraph_id: slice.paragraph_id,
        line_start: slice.line_start,
        line_end: slice.line_end,
        actual_lines: actualLineCount,
        extracted_lines: sliceLines.length
      });

      // ⚠️ DIAGNOSTIC: Log when slice produces no lines but paragraph has lines
      if (sliceLines.length === 0 && actualLineCount > 0) {
        CanvasLogger.lines.sliceEmpty(pageIndex, {
          slice_line_start: slice.line_start,
          slice_line_end: slice.line_end,
          safe_line_start: safeLineStart,
          safe_line_end: safeLineEnd,
          paragraph_id: slice.paragraph_id,
          actualLineCount
        });

        // 🔧 FALLBACK: If this is page 0 and we got no lines, use all lines
        // This prevents the "disappearing text" bug when pagination is stale
        if (pageIndex === 0 && lines.length === 0) {
          CanvasLogger.lines.sliceExtraction(pageIndex, actualLineCount, {
            phase: 'page0_fallback',
            reason: 'using_all_lines_from_paragraph'
          });
          lines.push(...paragraphSnapshot.lines);
        }
      } else {
        lines.push(...sliceLines);
      }
    }
  }

  // 🐛 DEBUG: Always log slice extraction summary
  CanvasLogger.lines.sliceExtraction(pageIndex, lines.length, {
    phase: 'complete',
    sliceBounds: sliceDebugInfo.map(s => `${s.line_start}-${s.line_end}`),
    sliceDebugInfo,
    cachedSnapshotLinesTotal: cachedSnapshots[0]?.lines?.length ?? 0,
    noLinesAfterSlicing: lines.length === 0 && (cachedSnapshots[0]?.lines?.length ?? 0) > 0
  });

  return {
    lines,
    caretMap: [], // OLD format doesn't include caret_map in page layout
    format: 'old',
    diagnostics: {
      rawLinesCount: lines.length,
      staleLinesFiltered: 0,
      sliceExtractionInfo: sliceDebugInfo,
      unmatchedSliceCount: unmatchedSlices.length
    }
  };
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Check if a page layout uses the new format (has resolved lines)
 */
export function isNewFormat(pageLayout: PageLayoutAny): pageLayout is PageLayoutNewFormat {
  return pageLayout?.lines && Array.isArray(pageLayout.lines);
}

/**
 * Check if a page layout uses the old format (has paragraph slices)
 */
export function isOldFormat(pageLayout: PageLayoutAny): pageLayout is PageLayoutOldFormat {
  return !isNewFormat(pageLayout) && pageLayout?.paragraphs && Array.isArray(pageLayout.paragraphs);
}

// =============================================================================
// Priority 2 Specific: Resolve from Props-Provided Pagination
// =============================================================================

/**
 * Options for resolving page lines from props-provided pagination (Priority 2)
 */
export interface Priority2ResolverOptions {
  /** Current page index being resolved */
  pageIndex: number;
  /** Total pages in the document */
  totalPages: number;
  /** Enable verbose logging */
  enableLogging?: boolean;
}

/**
 * Resolve page lines from props-provided pagination data (Priority 2 path).
 * 
 * This handles the case where pageData comes from shared worker pagination,
 * which ALWAYS uses the OLD format (paragraph slices). It includes special
 * handling for ID mismatch scenarios in single-page documents.
 * 
 * @param pageData - Page data from paginationResult (always OLD format with .paragraphs)
 * @param cachedSnapshots - Cached paragraph snapshots from TypesetterWorkerClient
 * @param options - Resolution options
 * @returns Resolved lines for this page (no caretMap - Priority 2 builds it separately)
 */
export function resolvePageLinesFromPropsData(
  pageData: { paragraphs?: ParagraphSlice[]; lines?: any[] },
  cachedSnapshots: CachedParagraphSnapshot[],
  options: Priority2ResolverOptions
): { lines: any[]; usedIdMismatchFallback: boolean } {
  const { pageIndex, totalPages, enableLogging = false } = options;

  // 🔥 FIX (2026-01-15): Check for NEW format (PageLayoutWithLines) first
  // WASM now returns pages with resolved lines directly, preventing ID mismatch bugs
  if (pageData?.lines && Array.isArray(pageData.lines)) {
    // 🔧 FIX (2026-01-23): Merge font info from cached snapshots into WASM lines.
    // WASM pagination lines have correct Y positions but wrong font_fingerprint (only Inter-Regular).
    // Cached snapshots from layoutAffectedParagraphs have correct font_fingerprint (Inter-Bold, etc).
    const cachedLinesByRange = new Map<string, any>();
    if (cachedSnapshots && cachedSnapshots.length > 0) {
      for (const snapshot of cachedSnapshots) {
        for (const line of (snapshot.lines || [])) {
          const key = `${line.start_grapheme_index}-${line.end_grapheme_index}`;
          cachedLinesByRange.set(key, line);
        }
      }
    }

    const mergedLines: any[] = [];
    let runsMerged = 0;

    for (const line of pageData.lines) {
      const key = `${line.start_grapheme_index}-${line.end_grapheme_index}`;
      const cachedLine = cachedLinesByRange.get(key);

      if (cachedLine && cachedLine.runs) {
        // Keep WASM's Y positions but use cached line's runs (with correct font_fingerprint)
        mergedLines.push({
          ...line,
          runs: cachedLine.runs
        });
        runsMerged++;
      } else {
        mergedLines.push(line);
      }
    }

    CanvasLogger.lines.sliceExtraction(pageIndex, mergedLines.length, {
      phase: 'p2-new-format',
      format: 'RESOLVED_LINES',
      message: 'Using NEW format with resolved lines from WASM - merged font info from cached snapshots'
    });

    // Log font merge stats
    if (runsMerged > 0) {
      CanvasLogger.glyph.warm('font-merge-p2', runsMerged, {
        pageIndex,
        totalLines: mergedLines.length,
        linesMerged: runsMerged,
        cachedLinesAvailable: cachedLinesByRange.size
      });
    }

    return {
      lines: mergedLines,
      usedIdMismatchFallback: false
    };
  }

  // OLD format handling (paragraph slices that need resolution)
  const lines: any[] = [];
  const slices = pageData?.paragraphs || [];

  // Log extraction start
  CanvasLogger.lines.sliceExtraction(pageIndex, 0, {
    phase: 'p2-start',
    format: 'OLD_PARAGRAPH_SLICES',
    totalPages,
    slicesCount: slices.length,
    cachedSnapshotsCount: cachedSnapshots.length,
    totalLinesInCache: cachedSnapshots.reduce((sum, s) => sum + (s?.lines?.length || 0), 0)
  });
  
  // 🔬 INSTRUMENTATION: Log all cached snapshots and their line ranges
  if (enableLogging) {
    CanvasLogger.lines.sliceExtraction(pageIndex, 0, {
      phase: 'p2-snapshots',
      snapshots: cachedSnapshots.map((s, i) => ({
        idx: i,
        id: s?.snapshot_id?.slice(0, 8) || 'unknown',
        linesCount: s?.lines?.length || 0
      })),
      slices: slices.map((s: any) => ({
        para_id: s.paragraph_id?.slice(0, 8) || 'unknown',
        line_start: s.line_start,
        line_end: s.line_end
      }))
    });
  }
  
  // 🔧 FIX: Check if slice IDs match cached snapshot IDs
  // WASM pagination may return generic IDs that don't match our per-paragraph snapshots
  const firstSliceId = slices[0]?.paragraph_id;
  const hasMatchingIds = firstSliceId && cachedSnapshots.some(s =>
    s.snapshot_id === firstSliceId || s.paragraph_id === firstSliceId
  );
  
  let usedIdMismatchFallback = false;
  
  if (!hasMatchingIds && totalPages === 1 && cachedSnapshots.length > 1) {
    // 🔧 ID MISMATCH FALLBACK: For single-page documents with multiple cached paragraphs,
    // use ALL lines from ALL snapshots since they all fit on page 0
    // 🔬 DIAGNOSTIC: Log FULL IDs (not truncated) to trace the mismatch
    CanvasLogger.lines.idMismatch(pageIndex, slices.length, {
      reason: 'p2_single_page_fallback',
      sliceIdFull: firstSliceId,  // Full ID from WASM slice
      sliceIdFormat: firstSliceId ? (firstSliceId.includes('-') ? 'UUID' : 'HEX') : 'NULL',
      cachedSnapshotIdsFull: cachedSnapshots.map(s => s.snapshot_id || s.paragraph_id),  // Full IDs
      cachedParagraphIdsFull: cachedSnapshots.map(s => s.paragraph_id),  // paragraph_id separately
      slicesDetail: slices.map((s: any) => ({
        paragraph_id: s.paragraph_id,
        line_start: s.line_start,
        line_end: s.line_end
      }))
    });
    
    for (const snap of cachedSnapshots) {
      if (snap?.lines) {
        lines.push(...snap.lines);
      }
    }
    usedIdMismatchFallback = true;
  } else {
    // Normal path: match slices to snapshots by ID
    for (const slice of slices) {
      // Find matching snapshot by snapshot_id OR paragraph_id
      const paragraphSnapshot = cachedSnapshots.find(s =>
        s.snapshot_id === slice.paragraph_id ||
        s.paragraph_id === slice.paragraph_id
      ) || cachedSnapshots[0];
      
      if (paragraphSnapshot?.lines) {
        const actualLineCount = paragraphSnapshot.lines.length;
        const safeLineStart = Math.min(slice.line_start, actualLineCount);
        const safeLineEnd = Math.min(slice.line_end, actualLineCount);
        
        const sliceLines = paragraphSnapshot.lines.slice(safeLineStart, safeLineEnd);
        lines.push(...sliceLines);
        
        if (enableLogging) {
          CanvasLogger.lines.sliceExtraction(pageIndex, sliceLines.length, {
            phase: 'p2-slice',
            paragraph_id: slice.paragraph_id,
            line_start: slice.line_start,
            line_end: slice.line_end
          });
        }
      }
    }
  }
  
  return { lines, usedIdMismatchFallback };
}
